import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectActiveWorkspaceConfig } from "../slices/workspaceConfigSlice";

/**
 * Single read-point for the workspace + country scoped Execution View UI config.
 *
 * The source is the active workspace's config
 * (`GET /workspaces/{id_client}/config`, held in the `workspaceConfig` slice).
 * The backend emits the raw `workspace_ui_config/{id_client}` doc opaquely; the
 * per-market resolution happens client-side here against the selected country.
 * The boot gate and the workspace switch both wait for that config, so it is
 * available before the Execution View metrics response lands and modals (Edit
 * Columns, Filters) that open before data arrives resolve the layout correctly.
 *
 * Schema (`executionView`):
 *   presets:   { <name>: <bundle> }     reusable config states, defined once
 *   byCountry: { <COUNTRY>: <name> }    market -> preset name (uppercase keys)
 *   default:   <name>                   optional fallback preset name
 *
 * A <bundle> carries the independent, market-level Execution View dimensions
 * returned by this hook:
 *   - hiddenColumns: string[]        columns to drop from the table
 *   - labels: { columns, sections }  canonical label catalog (overrides; fall back to code defaults)
 *   - columnOrder: object            reorder directives (e.g. { core_availability: "endOfAvailabilityGroup" })
 *   - brandMetrics: boolean          per-brand "SKU score" sections + brand_pct_* columns
 *   - hierarchicalFilter: { [column]: true }  filter columns drawn as a hierarchy ({ segment_type: true })
 *   - defaultColumns: string[]       extra columns shown without opening Edit columns
 *   - hideAiColumns: boolean         hide AI (custom_*) columns + the AI Column button + the Filters "AI Columns" tab
 *   - hideBrandFilter: boolean       hide the Filters "Brand filter" tab
 *   - salesTerritory: boolean        Sales Region / Territory filters and default columns
 *
 * `hideAiColumns` / `hideBrandFilter` are gated, not removed: a per-visit "Show
 * advanced tools" switch reveals whatever they hide (same escape-hatch pattern
 * as Market Overview's "Show all KPIs"). The switch shows whenever either is set.
 *
 * `labels` is a canonical catalog (the i18n / message-catalog pattern): config
 * holds the semantic label per logical column/section, looked up with a code
 * default fallback; surface-specific *formatting* (the "%" suffix, "≥ 80%",
 * "SKU score: <col>" composition) is composed in code at each consumer, never
 * stored in config.
 *
 * Resolution for the selected country X (uppercased):
 * `name = byCountry[X] ?? default`; `bundle = presets[name]`. No doc / no
 * country / no match / dangling reference / malformed shape all resolve to
 * inert defaults (every access optional-chained — never throws, never
 * half-applies a layout).
 *
 * `brandMetrics` and `salesTerritory` are the cross-stack dimensions. The
 * brand-metrics column definition + parsing live in backend code
 * (`ClientFeatureRegistry.BRAND_METRICS_LAYOUT`, apps/api). `salesTerritory` is
 * the same flag that tells the pipeline to assign territories and the export to
 * carry them. Every reader gates on the same per-env doc. The other dimensions
 * are pure frontend presentation.
 *
 * Schema (`marketOverview`): a flat per-workspace object (NOT per-country) with
 * snake_case keys, resolved here to camelCase:
 *   - visible_trend_metrics -> visibleTrendMetrics: string[]  (default [])
 *   - hidden_table_metrics  -> hiddenTableMetrics:  string[]  (default [])
 *   - sole_market_country   -> soleMarketCountry:   string|null (default null)
 * Absent / malformed values resolve to inert defaults, so an unconfigured
 * workspace is unchanged. This bundle resolves independently of `executionView`.
 *
 * Schema (`physicalFranchise`): a flat per-workspace object (NOT per-country),
 * same shape discipline as `marketOverview`:
 *   - visible_trend_metrics -> visibleTrendMetrics: string[] (default [])
 *   - hidden_columns        -> hiddenColumns:       string[] (default [])
 *   - hide_top_filters      -> hideTopFilters:      boolean  (default false)
 *   - hide_ai_columns       -> hideAiColumns:       boolean  (default false)
 *
 * `visible_trend_metrics` is an allow-list keyed on the trend response's own
 * **field names** (`pct_availability`, `pct_combo_fixed`, …), not on display
 * labels. The Physical Franchise picker derives its labels from the data and
 * this workspace's combo labels override two of them, so a label-keyed
 * allow-list would invalidate itself. The page inverts it into a suppression
 * list. An empty array suppresses nothing.
 *
 * Everything these four keys hide is revealed by a per-visit "Show advanced
 * tools" switch, the same escape hatch `hideAiColumns` / `hideBrandFilter` use
 * on Execution View.
 *
 * @returns {{
 *   hiddenColumns: string[],
 *   labels: { columns: object, sections: object },
 *   columnOrder: object,
 *   brandMetrics: boolean,
 *   hierarchicalFilter: Record<string, true>,
 *   defaultColumns: string[],
 *   hideAiColumns: boolean,
 *   hideBrandFilter: boolean,
 *   salesTerritory: boolean,
 *   marketOverview: {
 *     visibleTrendMetrics: string[],
 *     hiddenTableMetrics: string[],
 *     soleMarketCountry: string | null,
 *   },
 *   physicalFranchise: {
 *     visibleTrendMetrics: string[],
 *     hiddenColumns: string[],
 *     hideTopFilters: boolean,
 *     hideAiColumns: boolean,
 *   },
 * }}
 */
const NO_HIERARCHICAL_FILTER = Object.freeze({});

const INERT = Object.freeze({
  hiddenColumns: [],
  labels: { columns: {}, sections: {} },
  columnOrder: {},
  brandMetrics: false,
  hierarchicalFilter: NO_HIERARCHICAL_FILTER,
  defaultColumns: [],
  hideAiColumns: false,
  hideBrandFilter: false,
  salesTerritory: false,
  marketOverview: Object.freeze({
    visibleTrendMetrics: [],
    hiddenTableMetrics: [],
    soleMarketCountry: null,
  }),
  physicalFranchise: Object.freeze({
    visibleTrendMetrics: [],
    hiddenColumns: [],
    hideTopFilters: false,
    hideAiColumns: false,
  }),
});

// Which filter columns draw as a hierarchy, as `{ [column]: true }`. Keyed on
// the column so a market can take one hierarchy without the others. A column
// appears only when its block says `enabled: true`, so a column turned off
// reads the same as a column nobody configured. Never throws.
const resolveHierarchicalFilter = (raw) => {
  if (!raw || typeof raw !== "object") return NO_HIERARCHICAL_FILTER;
  const on = Object.entries(raw).filter(
    ([, value]) => value?.enabled === true
  );
  if (!on.length) return NO_HIERARCHICAL_FILTER;
  return Object.fromEntries(on.map(([column]) => [column, true]));
};

// Resolve the flat `marketOverview` bundle to a total, safe camelCase shape.
// Never throws; an absent or malformed value falls back to the inert default.
const resolveMarketOverview = (mo) => {
  if (!mo || typeof mo !== "object") return INERT.marketOverview;
  return {
    visibleTrendMetrics: Array.isArray(mo.visible_trend_metrics)
      ? mo.visible_trend_metrics
      : [],
    hiddenTableMetrics: Array.isArray(mo.hidden_table_metrics)
      ? mo.hidden_table_metrics
      : [],
    soleMarketCountry:
      typeof mo.sole_market_country === "string"
        ? mo.sole_market_country
        : null,
  };
};

// Resolve the flat `physicalFranchise` bundle to a total, safe camelCase shape.
// Never throws; an absent or malformed value falls back to the inert default.
const resolvePhysicalFranchise = (pf) => {
  if (!pf || typeof pf !== "object") return INERT.physicalFranchise;
  return {
    visibleTrendMetrics: Array.isArray(pf.visible_trend_metrics)
      ? pf.visible_trend_metrics
      : [],
    hiddenColumns: Array.isArray(pf.hidden_columns) ? pf.hidden_columns : [],
    hideTopFilters: pf.hide_top_filters === true,
    hideAiColumns: pf.hide_ai_columns === true,
  };
};

const selectWorkspaceUiConfig = (state) =>
  selectActiveWorkspaceConfig(state)?.uiConfig;

// The Execution View bundle a market resolves to, or null when none applies.
const resolveExecutionViewBundle = (uiConfig, country) => {
  const ev = uiConfig?.executionView;
  if (!ev || typeof ev !== "object") return null;
  const upper = country ? String(country).toUpperCase() : null;
  const name = (upper ? ev.byCountry?.[upper] : undefined) ?? ev.default;
  const bundle = name ? ev.presets?.[name] : undefined;
  return bundle && typeof bundle === "object" ? bundle : null;
};

/**
 * Whether one market carries the client's sales territories.
 *
 * Takes the market explicitly because a surface such as Map Explorer shows a
 * country of its own, which need not be the one Execution View has selected.
 */
export const useSalesTerritoryEnabled = (country) => {
  const uiConfig = useSelector(selectWorkspaceUiConfig);
  return (
    resolveExecutionViewBundle(uiConfig, country)?.salesTerritory?.enabled ===
    true
  );
};

// The active workspace's navigation settings. The API resolves them with their
// defaults, so the hooks below read finished values. Until a config loads, or
// when it fails to, each hook supplies the default itself.
const selectNavigation = (state) =>
  selectActiveWorkspaceConfig(state)?.navigation ?? null;

/**
 * Whether the workspace's dashboards render the top-bar navigation instead of
 * the sidebar.
 *
 * The boot gate and the workspace switch both wait for the config, so the
 * shell is known before the first dashboard paint. A config that failed to
 * load keeps the sidebar.
 */
export const useTopBarNavigation = () =>
  useSelector((state) => selectNavigation(state)?.top_bar ?? false);

/** Whether the dashboards show the Questions panel. */
export const useQuestionsPanelEnabled = () =>
  useSelector((state) => selectNavigation(state)?.questions_panel ?? false);

const DEFAULT_WORKFLOWS_LABEL = Object.freeze({
  title: "Workflows",
  noun: "workflows",
});

/**
 * The name the navigation gives the workflows section, which a client can
 * change, e.g. to "Use Cases". `title` heads a menu or section; `noun` sits
 * inside a sentence ("More use cases").
 */
export const useWorkflowsLabel = () =>
  useSelector(
    (state) => selectNavigation(state)?.workflows_label ?? DEFAULT_WORKFLOWS_LABEL
  );

export const useWorkspaceUiConfig = () => {
  const uiConfig = useSelector(selectWorkspaceUiConfig);
  const selectedCountry = useSelector(
    (state) => state.filters.currentCountry ?? state.outlet.pageInfo?.country
  );

  // Memoize so the returned object/array references stay stable across renders
  // when the inputs are unchanged. Consumers put `labels` / `hiddenColumns` /
  // `columnOrder` in effect + memo dependency arrays; a fresh reference every
  // render would re-fire those effects (and, where they setState, loop).
  return useMemo(() => {
    // Market Overview and Physical Franchise config are flat (not per-country)
    // and independent of the Execution View bundle, so resolve them regardless
    // of `executionView`.
    const marketOverview = resolveMarketOverview(uiConfig?.marketOverview);
    const physicalFranchise = resolvePhysicalFranchise(
      uiConfig?.physicalFranchise
    );

    const bundle = resolveExecutionViewBundle(uiConfig, selectedCountry);
    if (!bundle) {
      return { ...INERT, marketOverview, physicalFranchise };
    }

    return {
      hiddenColumns: Array.isArray(bundle.hiddenColumns)
        ? bundle.hiddenColumns
        : [],
      labels: {
        columns: bundle.labels?.columns ?? {},
        sections: bundle.labels?.sections ?? {},
      },
      columnOrder: bundle.columnOrder ?? {},
      brandMetrics: bundle.brandMetrics?.enabled === true,
      hierarchicalFilter: resolveHierarchicalFilter(bundle.hierarchicalFilter),
      defaultColumns: Array.isArray(bundle.defaultColumns)
        ? bundle.defaultColumns
        : [],
      hideAiColumns: bundle.hideAiColumns === true,
      hideBrandFilter: bundle.hideBrandFilter === true,
      salesTerritory: bundle.salesTerritory?.enabled === true,
      marketOverview,
      physicalFranchise,
    };
  }, [uiConfig, selectedCountry]);
};
